<template>
  <v-tooltip bottom v-if="autoCloseWarning" max-width="320" content-class="autoclose-tooltip">
    <template v-slot:activator="{ on, attrs }">
      <span
        class="pill pill--autoclose"
        :class="{ 'pill--autoclose-urgent': autoCloseWarning.urgent }"
        v-bind="attrs"
        v-on="on"
      >
        <v-icon size="10" :color="autoCloseWarning.urgent ? '#92400E' : '#1E40AF'">mdi-timer-sand</v-icon>
        <div class="pill-text" :class="{ 'pill-text--vertical': small }">
          <span v-if="autoCloseWarning.closesAt">
            Cierre automático <strong>{{ autoCloseWarning.dateLabel }}</strong>
          </span>
          <span v-else>{{ autoCloseWarning.remainingText }}</span>
        </div>
      </span>
    </template>

    <div class="autoclose-tooltip-body">

      <div class="act-row act-title">
        <v-icon size="13" color="#1E40AF">mdi-timer-sand</v-icon>
        <span>Cierre automático configurado</span>
      </div>

      <div class="act-divider" />

      <div class="act-row">
        <v-icon size="12" color="#6B7280">mdi-clock-outline</v-icon>
        <span class="act-label">Plazo configurado</span>
        <span class="act-value">{{ projectConfig.rdi_approval_hours_limit }}h hábiles</span>
      </div>

      <div class="act-row">
        <v-icon size="12" color="#6B7280">mdi-calendar-check-outline</v-icon>
        <span class="act-label">Días hábiles</span>
        <span class="act-value">{{ workScheduleDaysLabel }}</span>
      </div>

      <div class="act-row">
        <v-icon size="12" color="#6B7280">mdi-clock-time-four-outline</v-icon>
        <span class="act-label">Horario</span>
        <span class="act-value">{{ workScheduleHoursLabel }}</span>
      </div>

      <div class="act-divider" />

      <div class="act-row">
        <v-icon size="12" :color="isCurrentlyWorkingHour ? '#15803D' : '#B45309'">
          {{ isCurrentlyWorkingHour ? 'mdi-briefcase-check-outline' : 'mdi-briefcase-off-outline' }}
        </v-icon>
        <span class="act-label">Ahora mismo</span>
        <span class="act-value" :class="isCurrentlyWorkingHour ? 'act-value--green' : 'act-value--orange'">
          {{ isCurrentlyWorkingHour ? 'En horario hábil' : 'Fuera de horario hábil' }}
        </span>
      </div>

      <div class="act-row" v-if="autoCloseWarning.closesAt">
        <v-icon size="12" color="#1E40AF">mdi-flag-checkered</v-icon>
        <span class="act-label">Se cierra</span>
        <span class="act-value act-value--blue">{{ autoCloseWarning.dateLabel }}</span>
      </div>

      <div class="act-note">
        El contador avanza solo dentro del horario hábil configurado, desde la última respuesta oficial.
      </div>

    </div>
  </v-tooltip>
</template>

<script>
import moment from 'moment'
import { calcWorkingHours, addWorkingHours, isWithinWorkingHours } from '@/helpers/workingHoursHelper'

export default {
  name: 'AutoCloseInfo',
  props: {
    rdi: { type: Object, required: true },
    projectConfig: { type: Object, required: true },
    now: { type: [String, Date], required: false, default: null },
    small: { type: Boolean, default: false }
  },
  computed: {
    autoCloseWarning() {
      const rdi = this.rdi
      if (!rdi || rdi.status === 3) return null

      const config = this.projectConfig
      const limitHours = config?.rdi_approval_hours_limit
      if (!limitHours) return null

      const schedule = config.work_schedule
      if (!schedule || schedule.length === 0) return null

      const interactions = [...(rdi.interactions || [])]
        .filter(i => i.user_id !== null)
        .sort((a, b) => moment(a.created_at).diff(moment(b.created_at)))

      if (interactions.length === 0) return null
      const lastInteraction = interactions[interactions.length - 1]
      if (!lastInteraction.is_response) return null

      const referenceTime = moment(lastInteraction.created_at)
      const now = this.now ? moment(this.now) : moment()

      const workedHours = calcWorkingHours(referenceTime, now, schedule)
      const remaining = limitHours - workedHours

      if (remaining <= 0) {
        return {
          urgent: true,
          closesAt: null,
          remainingText: 'Cierre automático pendiente'
        }
      }

      const closeAt = addWorkingHours(now, remaining, schedule)

      const isToday    = closeAt.isSame(now, 'day')
      const isTomorrow = closeAt.isSame(now.clone().add(1, 'day'), 'day')

      let dateLabel = ''
      if (isToday) {
        dateLabel = `hoy a las ${closeAt.format('HH:mm')}`
      } else if (isTomorrow) {
        dateLabel = `mañana a las ${closeAt.format('HH:mm')}`
      } else {
        dateLabel = closeAt.format('DD/MM/YY [a las] HH:mm')
      }

      const urgent = remaining <= limitHours * 0.25

      return { urgent, closesAt: closeAt, dateLabel }
    },

    isCurrentlyWorkingHour() {
      const schedule = this.projectConfig?.work_schedule
      if (!schedule || schedule.length === 0) return false
      const now = this.now ? moment(this.now) : moment()
      return isWithinWorkingHours(now, schedule)
    },

    workScheduleDaysLabel() {
      const schedule = this.projectConfig?.work_schedule
      if (!schedule || schedule.length === 0) return '—'
      const dayNames = { 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb', 7: 'Dom' }
      return schedule
        .slice()
        .sort((a, b) => a.day - b.day)
        .map(s => dayNames[s.day] || s.day)
        .join(', ')
    },

    workScheduleHoursLabel() {
      const schedule = this.projectConfig?.work_schedule
      if (!schedule || schedule.length === 0) return '—'
      const first = schedule[0]
      return `${first.start_working_hour} – ${first.end_working_hour}`
    }
  }
}
</script>

<style scoped>
.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.pill--autoclose strong { font-weight: 700; }
.pill-text {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
}
.pill-text--vertical {
  flex-direction: column;
  align-items: flex-start;
  gap: 0px;
  white-space: normal;
  font-size: 9px;
  line-height: 1.2;
}
.autoclose-tooltip-body { padding: 10px; max-width: 300px; }
.act-row { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.act-title { font-weight:600; color:#1E40AF; }
.act-divider { height:1px; background:#E5E8EF; margin:8px 0; }
.act-label { color:#6B7280; font-size:12px; flex:1; }
.act-value { font-size:12px; font-weight:600; }
.act-note { font-size:12px; color:#6B7280; margin-top:6px; }
.pill--autoclose { background:#EEF2FF; color:#1E40AF; border:1px solid #C7D2FE; padding:3px 8px; border-radius:999px; display:inline-flex; align-items:center; gap:4px; }
.pill--autoclose-urgent { background:#FFFBEB; color:#B45309; border:1px solid #FDE68A; }
.act-value--green { color:#15803D; font-weight:600; }
.act-value--orange { color:#B45309; font-weight:600; }
.act-value--blue { color:#1E40AF; font-weight:600; }
</style>
<style>
.autoclose-tooltip {
  background: #FFFFFF !important;
  border: 1.5px solid #93C5FD !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 16px rgba(30, 64, 175, 0.10) !important;
  padding: 0 !important;
  opacity: 1 !important;
}
.autoclose-tooltip::after,
.autoclose-tooltip::before {
  display: none !important;
}
@keyframes pulse-border {
  0%, 100% { border-color: #FCD34D; box-shadow: none; }
  50% { border-color: #F59E0B; box-shadow: 0 0 0 2px rgba(245,158,11,0.15); }
}
.autoclose-tooltip-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 240px;
}
.act-title {
  font-size: 12px;
  font-weight: 600;
  color: #1E40AF;
  gap: 6px;
}
.act-divider {
  height: 1px;
  background: #E0EAFF;
  margin: 2px 0;
}
.act-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #374151;
}
.act-label {
  color: #6B7280;
  flex: 1;
}
.act-value {
  font-weight: 600;
  color: #1A1D23;
  text-align: right;
}
.act-value--green  { color: #15803D; }
.act-value--orange { color: #B45309; }
.act-value--blue   { color: #1E40AF; }
.act-note {
  margin-top: 4px;
  font-size: 10.5px;
  color: #9CA3AF;
  line-height: 1.5;
  border-top: 1px solid #F3F4F6;
  padding-top: 6px;
}
</style>